import type { Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '../../shared/protocol.ts';

/** Coupure plus courte que ça : rien n'est montré (la reconnexion est souvent immédiate). */
const DISCONNECT_GRACE_MS = 800;
/** Battement de cœur envoyé au serveur à ce rythme, une fois le précédent répondu. */
const PING_INTERVAL_MS = 3000;
/** Réponse au battement plus lente que ça : connexion lente. */
const SLOW_PING_MS = 2500;

/**
 * Petit indicateur de chargement en haut de l'écran : connexion coupée (socket.io se reconnecte tout seul) ou serveur
 * qui tarde à répondre.
 */
export class ConnectionIndicator {
  private readonly element: HTMLElement;
  private readonly label: HTMLElement;
  private disconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private pingTimer: ReturnType<typeof setTimeout> | undefined;
  private slowTimer: ReturnType<typeof setTimeout> | undefined;
  /** Numéro du battement en attente de réponse : une réponse arrivée après une reconnexion est ignorée. */
  private pingId = 0;
  private stopped = false;
  private readonly socket: Socket<ServerToClientEvents, ClientToServerEvents>;

  constructor(root: HTMLElement, socket: Socket<ServerToClientEvents, ClientToServerEvents>) {
    this.socket = socket;
    this.element = root.querySelector<HTMLElement>(':scope > .connection-indicator') ?? root.appendChild(Object.assign(document.createElement('div'), {
      className: 'connection-indicator', hidden: true, role: 'status', innerHTML: '<span class="connection-spinner" aria-hidden="true"></span><span></span>',
    }));
    this.label = this.element.lastElementChild as HTMLElement;
    this.element.hidden = true;
    socket.on('connect', () => {
      clearTimeout(this.disconnectTimer);
      this.hide();
      this.ping();
    });
    socket.on('disconnect', () => {
      this.resetPing();
      if (this.stopped) return;
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = setTimeout(() => this.show('Reconnexion…'), DISCONNECT_GRACE_MS);
    });
  }

  stop(): void {
    this.stopped = true;
    clearTimeout(this.disconnectTimer);
    this.resetPing();
    this.hide();
  }

  private ping(): void {
    this.resetPing();
    if (this.stopped || !this.socket.connected) return;
    const id = ++this.pingId;
    this.slowTimer = setTimeout(() => this.show('Connexion lente…'), SLOW_PING_MS);
    this.socket.emit('heartbeat', () => {
      if (id !== this.pingId) return;
      clearTimeout(this.slowTimer);
      if (this.socket.connected) this.hide();
      this.pingTimer = setTimeout(() => this.ping(), PING_INTERVAL_MS);
    });
  }

  private resetPing(): void {
    this.pingId++;
    clearTimeout(this.pingTimer);
    clearTimeout(this.slowTimer);
  }

  private show(text: string): void {
    this.label.textContent = text;
    this.element.hidden = false;
  }

  private hide(): void {
    this.element.hidden = true;
  }
}
